import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SPACE_ELEMENTS_OF_MODAL, STACK_ERROR_SX } from "@components/common/dialog/dialog-constants";
import { GroupAutoComplete } from "@components/common/group-autocomplete/group-autocomplete";
import { LabelWithHelp } from "@components/common/label-with-help";
import { MemberAutocomplete } from "@components/common/member-autocomplete/member-autocomplete";
import { useGroupMembersContext } from "@context-providers/group-members-provider";
import { useCompanyId } from "@context-providers/company-id-provider";
import { Alert, Dialog, Tooltip } from "@flat-ui";
import { useResolveProjectManagerIdentity } from "@hooks/projects/use-resolve-project-manager-identity";
import { Stack } from "@mui-wrapper";
import { groupsSelector } from "@store/groups/groups-selector";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useEffect, useRef, useState } from "react";
/** Dialog to change the project manager and group of the current project */
export function ChangeManagementDialogContent({
  isOpen,
  onClose
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    canAssignManagers,
    resolveProjectManagerIdentity
  } = useResolveProjectManagerIdentity();
  const {
    project,
    groupMembers,
    setSelectedGroupId,
    selectedGroupId
  } = useGroupMembersContext();
  const groups = useAppSelector(groupsSelector);

  // Hide the confirm action only when the sole group is already the project's group (nothing to
  // change); an ungrouped project must still be assignable to that lone group.
  const hasNoAlternativeGroup = !canAssignManagers && groups.length === 1 && project?.group?.id === groups[0].id;
  const [selectedProjectManager, setSelectedProjectManager] = useState(null);
  const isSubmittingRef = useRef(false);
  const [isChangingManagement, setIsChangingManagement] = useState(false);

  // Reset the group selection to the project's current group whenever the dialog opens, so a
  // previously changed-then-cancelled selection does not persist across opens. Projects without a
  // group must reset to undefined so a stale selection is not submitted unintentionally.
  useEffect(() => {
    if (isOpen) {
      setSelectedGroupId(project?.group?.id);
    }
  }, [isOpen, project?.group, setSelectedGroupId]);

  // Prefill the project manager with the current project manager, if the current group of the project is selected
  // Empty it for other groups
  useEffect(() => {
    if (project && isOpen && project.group) {
      if (project.group.id !== selectedGroupId) {
        setSelectedProjectManager(null);
        return;
      }
      const projectManager = groupMembers.find(member => member.identity === project.managers?.projectManager?.identity) ?? null;
      setSelectedProjectManager(projectManager);
    }
  }, [isOpen, groupMembers, project, selectedGroupId]);
  useEffect(() => {
    if (isOpen) {
      isSubmittingRef.current = false;
    }
  }, [isOpen]);

  /**
   * Sends a request to the backend to change the project manager and group of the project
   */
  async function changeProjectManagerAndGroup() {
    // Synchronous guard against very fast double-clicks before state rerender.
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    // When the workspace can assign managers the manager is chosen in the dialog. Otherwise the
    // manager selection is hidden and the current user (main admin) is used implicitly.
    const managerIdentity = resolveProjectManagerIdentity(selectedProjectManager);

    // Theoretically the user should not be able to reach this state
    // if the manager was not resolved but do this for type safety
    // and as an edge case if our members selection didn't work properly.
    if (!managerIdentity) {
      throw new Error("Project manager is required!");
    }
    setIsChangingManagement(true);
    try {
      // Only invited-by-email managers (plain string identities) need to be added to the company
      // first. When the current user is used implicitly they are already a member.
      if (canAssignManagers && typeof selectedProjectManager === "string") {
        const payload = {
          assignments: [{
            identities: [selectedProjectManager],
            role: "PROJECT_MANAGER"
          }]
        };
        await faroCoreApiClient.company.addMembersToCompany({
          companyId,
          payload
        });
      }
      const payload = {
        projectManagerIdentity: managerIdentity,
        groupId: String(selectedGroupId)
      };
      await dispatch(updateProjectDetails({
        projectId: project?.id,
        payload
      })).unwrap();
      onClose();
    } finally {
      setIsChangingManagement(false);
    }
  }
  return <Dialog title={canAssignManagers ? "Change Management" : "Change Group"} confirmText="Change" open={isOpen} onConfirm={hasNoAlternativeGroup ? undefined : changeProjectManagerAndGroup} isConfirmDisabled={(canAssignManagers ? !selectedProjectManager : !selectedGroupId) || isChangingManagement} onCancel={isChangingManagement ? undefined : onClose} showSpinner={isChangingManagement} disabled={isChangingManagement}>
      {canAssignManagers && <Alert variant="info" title="Be aware that changing a group can ultimately impact the project manager." sx={{
      marginBottom: SPACE_ELEMENTS_OF_MODAL
    }} />}
      <Stack sx={STACK_ERROR_SX}>
        <LabelWithHelp title="Group" />
        <Tooltip component="div" title={hasNoAlternativeGroup ? "There is no other group available to move this project to." : ""} tooltipSx={{
        maxWidth: "min(24rem, calc(100vw - 2rem))"
      }}>
          <GroupAutoComplete callbackOnGroupSelected={groupId => setSelectedGroupId(groupId)} selectedGroupId={selectedGroupId}
        // A group manager only matters when the manager is derived from the group; the
        // Change Group flow uses the current admin implicitly, so allow every group there.
        shouldOnlyShowGroupsWithGroupManager={canAssignManagers} />
        </Tooltip>
      </Stack>
      {canAssignManagers && <Stack>
          <LabelWithHelp title="Manager" isDisabled={!selectedGroupId} />
          <MemberAutocomplete members={groupMembers} selectedMember={selectedProjectManager} onOptionChange={setSelectedProjectManager} isDisabled={!selectedGroupId} placeholder="Choose who can manage this project..." />
        </Stack>}
    </Dialog>;
}